<template>
  <div class="claim-field-row" :class="{'is-editing': editing, 'is-overridden': field.isOverridden, [`status-${field.status}`]: true}">
    <div class="cell cell-loop">
      <input type="text" class="form-control form-control-sm cell-input readonly" :value="field.loop" :title="field.loop" readonly tabindex="-1"/>
    </div>

    <div class="cell cell-element">
      <input type="text" class="form-control form-control-sm cell-input readonly" :value="field.element" readonly tabindex="-1"/>
    </div>

    <div class="cell cell-description">
      <input type="text" class="form-control form-control-sm cell-input readonly" :value="field.description" :title="field.description" readonly tabindex="-1"/>
    </div>

    <div class="cell cell-value">
      <input ref="inputRef"
             type="text"
             class="form-control form-control-sm cell-input"
             :class="{'is-empty': isEmpty && !editing}"
             :value="editing ? draft : displayValue"
             :readonly="!editing"
             :tabindex="editing ? 0 : -1"
             :data-test="`inputField-${field.id}`"
             @input="onInput"
             @keydown.enter.prevent="commit"
             @keydown.esc.prevent="cancel"/>
    </div>

    <!-- Coluna 5: Ações -->
    <div class="cell cell-actions">
      <template v-if="!editing">
        <button v-if="field.editable" type="button" class="icon-btn icon-edit" :title="$t('edit')" :data-test="`btnEditField-${field.id}`" @click="startEdit">
          <i class="fa fa-pencil"></i>
        </button>
        <span v-else class="icon-btn icon-readonly" :title="$t('clinicClaim.readonly')">
          <i class="fa fa-lock"></i>
        </span>
      </template>

      <template v-else>
        <button type="button" class="icon-btn icon-confirm" :title="$t('save')" :data-test="`btnSaveField-${field.id}`" @click="commit">
          <i class="fa fa-check"></i>
        </button>
        <button type="button" class="icon-btn icon-cancel" :title="$t('cancel')" :data-test="`btnCancelField-${field.id}`" @click="cancel">
          <i class="fa fa-times"></i>
        </button>
        <button v-if="field.isOverridden" type="button" class="icon-btn icon-reset" :title="$t('clinicClaim.resetTooltip')" :data-test="`btnResetField-${field.id}`" @click="resetToDefault">
          <i class="fa fa-undo"></i>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EdiClaimFieldRow',
  props: {
    field: {type: Object, required: true}
  },
  emits: ['change'],
  data() {
    return {
      editing: false,
      draft: ''
    }
  },
  computed: {
    isEmpty() {
      return this.field.currentValue == null || this.field.currentValue === ''
    },
    displayValue() {
      if (this.isEmpty) return ''
      return String(this.field.currentValue)
    }
  },
  methods: {
    onInput(e) {
      this.draft = e.target.value
    },
    startEdit() {
      this.draft = this.field.currentValue == null ? '' : String(this.field.currentValue)
      this.editing = true
      this.$nextTick(() => {
        if (this.$refs.inputRef) {
          this.$refs.inputRef.focus()
          this.$refs.inputRef.select()
        }
      })
    },
    commit() {
      const value = (this.draft || '').trim()
      this.editing = false
      this.$emit('change', {id: this.field.id, value})
    },
    cancel() {
      this.editing = false
      this.draft = ''
    },
    resetToDefault() {
      this.editing = false
      this.$emit('change', {id: this.field.id, value: ''})
    }
  }
}
</script>

<style lang="scss" scoped>
.claim-field-row {
  display:                     grid;
  grid-template-columns:       110px 110px minmax(200px, 2fr) minmax(200px, 2fr) 90px;
  gap:                         8px;
  padding:                     6px 4px;
  align-items:                 center;
  font-size:                   13px;
  border-bottom:               1px solid var(--Shades-20-Border);

  &:hover {
    background:                var(--Shades-10-H);
  }

  &.is-editing {
    background:                var(--Shades-10-H);

    .cell-value .cell-input:not(.readonly) {
      border-color:            var(--Brand-Secondary);
      background:              var(--Shades-BG);
    }
  }

  &.is-overridden .cell-value .cell-input {
    color:                     var(--Brand-Secondary) !important;
    font-weight:               600;
  }

  &.status-error .cell-loop .cell-input {
    border-left:               3px solid var(--Support-Error);
  }
  &.status-fix .cell-loop .cell-input {
    border-left:               3px solid var(--Complementary-Yellow);
  }
  &.status-remove {
    opacity:                   0.5;
  }
}

.cell {
  min-width:                   0;

  .cell-input {
    width:                     100%;
    height:                    32px;
    padding:                   4px 10px;
    font-size:                 12px;
    font-family:               'Monaco', 'Menlo', monospace;
    border-radius:             4px;
    background:                var(--Shades-BG);
    border:                    1px solid var(--Shades-30-Border);
    color:                     var(--Brand-Primary);

    &:focus {
      outline:                 none;
      border-color:            var(--Brand-Secondary);
      box-shadow:              0 0 0 2px rgba(60, 179, 145, 0.2);
    }

    &.readonly {
      background:              var(--Shades-10);
      cursor:                  default;
      color:                   var(--Shades-60-W);
    }

    &.is-empty {
      color:                   var(--Shades-50);
      font-style:              italic;
    }
  }
}

.cell-loop .cell-input {
  font-weight:                 600;
  color:                       var(--Support-Primary-Success) !important;
}

.cell-element .cell-input {
  font-weight:                 600;
}

.cell-description .cell-input {
  font-family:                 inherit;
  text-overflow:               ellipsis;
  white-space:                 nowrap;
  overflow:                    hidden;
}

.cell-actions {
  display:                     flex;
  gap:                         6px;
  justify-content:             flex-start;
  align-items:                 center;
}

.icon-btn {
  background:                  transparent;
  border:                      none;
  width:                       28px;
  height:                      28px;
  border-radius:               4px;
  display:                     inline-flex;
  align-items:                 center;
  justify-content:             center;
  cursor:                      pointer;
  font-size:                   13px;
  transition:                  background 0.15s ease, color 0.15s ease;
  padding:                     0;

  &:hover {
    background:                var(--Shades-20);
  }

  &.icon-edit {
    color:                     var(--Shades-60-W);
    &:hover { color: var(--Brand-Secondary); }
  }
  &.icon-confirm {
    color:                     var(--Support-Success);
    &:hover { background: var(--Complementary-Success-BG); }
  }
  &.icon-cancel {
    color:                     var(--Support-Error);
    &:hover { background: var(--Complementary-Orange-BG); }
  }
  &.icon-reset {
    color:                     var(--Complementary-Orange);
    &:hover { background: var(--Complementary-Orange-BG); }
  }
  &.icon-readonly {
    color:                     var(--Shades-50);
    cursor:                    default;
    &:hover { background: transparent; }
  }
}
</style>
